// Merchant GMV (FIG-345 item 1 · UC-230). Shared by the Merchants list and the
// compute-merchant-gmv-v1 / merchant-gmv-v1 edge functions, so it has no imports.
//
// Each merchant's GMV is the ∑: Variants TOTAL ("Total GMV $") with the
// merchant's saved Variants source (UC-229), up to midnight Pacific time at the
// start of today, i.e. the report run "as of" yesterday's Pacific date:
//   - Trailing 12M: the report's own Trailing 12 months, as of that date;
//   - All Time: the same totals from ALL_TIME_START_DATE to that date.

export const GMV_TIME_ZONE = 'America/Los_Angeles';

/** Earlier than any sales data in EVE; the All Time window starts here. */
export const ALL_TIME_START_DATE = '2000-01-01';

/** 'YYYY-MM-DD' for the calendar day of `at` in `timeZone`. */
export function dateInTimeZone(at: Date, timeZone: string = GMV_TIME_ZONE): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(at);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Adds whole days to a 'YYYY-MM-DD' date (calendar arithmetic, no time zone). */
export function addDays(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d + days));
  return date.toISOString().slice(0, 10);
}

/**
 * The report "as of" date for a run at `now`: yesterday in Pacific time. The
 * figures then run to midnight PT at the start of today (AC-022: on 14 Oct
 * 2026 the cut-off is 14 Oct 2026 00:00 PT and the as-of date is 13 Oct 2026).
 */
export function gmvAsOfDate(now: Date): string {
  return addDays(dateInTimeZone(now), -1);
}

/**
 * The UTC instant of midnight Pacific time at the start of `ymd`, as
 * an ISO timestamp ('YYYY-MM-DDTHH:00:00Z', 07:00 or 08:00 UTC depending on
 * daylight saving) that both Postgres and Date.parse read.
 * Used as the All Time end bound so the live queries, which compare timestamps,
 * include all of the as-of day (Greptile P1 on #292).
 */
export function pacificMidnightUtc(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  for (const hour of [7, 8]) {
    const candidate = new Date(Date.UTC(y, m - 1, d, hour));
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: GMV_TIME_ZONE, hour: '2-digit', hourCycle: 'h23' }).formatToParts(candidate);
    const ptHour = Number(parts.find((p) => p.type === 'hour')?.value);
    if (ptHour === 0 && dateInTimeZone(candidate) === ymd) {
      return `${ymd}T${String(hour).padStart(2, '0')}:00:00Z`;
    }
  }
  return `${ymd}T08:00:00Z`;
}

/** The cut-off a stored as-of date stands for: midnight PT at the start of the next day. */
export function gmvCutoffDate(asOf: string): string {
  return addDays(asOf, 1);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "midnight PT, 14 Oct 2026" for an as-of date of 2026-10-13. */
export function describeGmvCutoff(asOf: string): string {
  const [y, m, d] = gmvCutoffDate(asOf).split('-').map(Number);
  return `midnight PT, ${d} ${MONTHS[m - 1]} ${y}`;
}

/** Report totals come as formatted strings ("1,234.56", "-12.00"). */
export function parseReportAmount(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const cleaned = value.replace(/[$,\s]/g, '');
  if (cleaned === '') return null;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

/** One merchant's latest computed figures, as merchant-gmv-v1 returns them. */
export interface MerchantGmvFigures {
  organisationId: string;
  /** The report "as of" date the figures were computed for. */
  asOf: string;
  /** Null when the merchant has no sales history ("No data"). */
  gmvAllTime: number | null;
  gmvT12m: number | null;
  hasSalesHistory: boolean;
  computedAt: string;
}

export interface MerchantGmvResponse {
  success: boolean;
  error?: string;
  merchants?: MerchantGmvFigures[];
  /** The newest as-of date any merchant has figures for. */
  latestAsOf?: string | null;
}

export type GmvColumn = 'gmvAllTime' | 'gmvT12m';

/** The value a list cell shows and sorts by; null means "No data". */
export function gmvValue(figures: MerchantGmvFigures | undefined, column: GmvColumn): number | null {
  if (!figures || !figures.hasSalesHistory) return null;
  return figures[column];
}

/**
 * Sort by a GMV column: highest or lowest first, with "No data" (and merchants
 * not computed yet) always last whichever the direction (AC-020).
 */
export function compareGmv(a: number | null, b: number | null, direction: 'asc' | 'desc'): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return direction === 'asc' ? a - b : b - a;
}

/** Sum of the values that exist; null when none do. */
export function sumGmv(values: Array<number | null>): number | null {
  const present = values.filter((v): v is number => v !== null);
  return present.length === 0 ? null : present.reduce((total, v) => total + v, 0);
}
